import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import api from "@/lib/api";
import {
  Mic, MicOff, Loader2, ArrowRight, ArrowLeft, CheckCircle2, RotateCcw,
  BookOpen, PencilLine, Image as ImageIcon, Clock,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Unified player for all exam question types.
 * Picks the right UI sub-component based on type_id.
 */

export const TYPE_META = {
  // PTE
  "read-aloud":      { skill: "speaking", title: "Read Aloud",      prepSec: 35, recordSec: 40,  scoreScale: "10–90" },
  "describe-image":  { skill: "speaking", title: "Describe Image",  prepSec: 25, recordSec: 40,  scoreScale: "10–90" },
  "repeat-sentence": { skill: "speaking", title: "Repeat Sentence", prepSec: 0,  recordSec: 15,  scoreScale: "10–90", hasAudio: true },
  "re-tell-lecture": { skill: "speaking", title: "Re-tell Lecture", prepSec: 10, recordSec: 40,  scoreScale: "10–90", hasAudio: true },
  "summarise-text":  { skill: "writing",  title: "Summarise Written Text", durationSec: 600, scoreScale: "10–90" },
  // Duolingo
  "read-complete":   { skill: "reading",  title: "Read & Complete", prepSec: 0,  durationSec: 180,scoreScale: "10–160" },
  "speak-photo":     { skill: "speaking", title: "Speak About the Photo", prepSec: 20, recordSec: 60, scoreScale: "10–160" },
  "write-photo":     { skill: "writing",  title: "Write About the Photo", prepSec: 0, durationSec: 60, scoreScale: "10–160" },
  "listen-type":     { skill: "listening", title: "Listen & Type",  durationSec: 120, scoreScale: "10–160", hasAudio: true },
  // Oxford ELLT
  "reading-mcq":     { skill: "reading",  title: "Reading Comprehension", durationSec: 240, scoreScale: "CEFR" },
  "speaking-prompt": { skill: "speaking", title: "Speaking Task", prepSec: 15, recordSec: 90, scoreScale: "CEFR" },
  "writing-task":    { skill: "writing",  title: "Writing Task", durationSec: 600, scoreScale: "CEFR" },
  "listening-mcq":   { skill: "listening", title: "Listening Comprehension", durationSec: 240, scoreScale: "CEFR", hasAudio: true },
};

export default function ExamPlayer({ examIdOverride }) {
  const params = useParams();
  const examId = examIdOverride || params.examId;
  const { typeId } = params;
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const meta = TYPE_META[typeId] || {};

  useEffect(() => {
    setLoading(true);
    api.get(`/exam/${examId}/items/${typeId}`)
      .then((r) => { setItems(r.data.items); setLoading(false); })
      .catch((e) => { toast.error(e.response?.data?.detail || "Failed to load items"); setLoading(false); });
  }, [examId, typeId]);

  if (loading) {
    return <DashboardLayout><div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div></DashboardLayout>;
  }
  if (!items.length) {
    return <DashboardLayout><div className="text-slate-500">No items available. <Link to={`/app/${examId}`} className="text-[#F77F00]">Back</Link></div></DashboardLayout>;
  }

  const item = items[idx];
  const next = () => { setResult(null); setIdx((i) => (i + 1) % items.length); };
  const prev = () => { setResult(null); setIdx((i) => (i - 1 + items.length) % items.length); };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid={`exam-player-${examId}-${typeId}`}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
              <Link to={`/app/${examId}`} className="hover:text-[#F77F00] inline-flex items-center gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to exam
              </Link>
              <span>·</span>
              <span className="uppercase tracking-wider">{meta.skill}</span>
              <span>·</span>
              <span>{meta.scoreScale}</span>
            </div>
            <h1 className="font-display text-3xl text-[#003049]">{meta.title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={prev} className="px-3 py-2 rounded-full border border-slate-200 hover:bg-slate-50 text-sm">Prev</button>
            <div className="text-sm text-slate-500 tabular-nums">{idx + 1} / {items.length}</div>
            <button onClick={next} className="px-3 py-2 rounded-full border border-slate-200 hover:bg-slate-50 text-sm">Next</button>
          </div>
        </div>

        {/* Player body */}
        {meta.skill === "speaking" && (
          <SpeakingPlayer examId={examId} typeId={typeId} item={item} meta={meta}
            onResult={setResult} submitting={submitting} setSubmitting={setSubmitting} />
        )}
        {typeId === "read-complete" && (
          <ReadCompletePlayer examId={examId} typeId={typeId} item={item} meta={meta}
            onResult={setResult} submitting={submitting} setSubmitting={setSubmitting} />
        )}
        {(typeId === "write-photo" || typeId === "writing-task" || typeId === "summarise-text") && (
          <WritingPlayer examId={examId} typeId={typeId} item={item} meta={meta}
            onResult={setResult} submitting={submitting} setSubmitting={setSubmitting} />
        )}
        {(typeId === "reading-mcq" || typeId === "listening-mcq") && (
          <MCQPlayer examId={examId} typeId={typeId} item={item} meta={meta}
            onResult={setResult} submitting={submitting} setSubmitting={setSubmitting} />
        )}
        {typeId === "listen-type" && (
          <ListenTypePlayer examId={examId} typeId={typeId} item={item} meta={meta}
            onResult={setResult} submitting={submitting} setSubmitting={setSubmitting} />
        )}

        {/* Result */}
        {result && <ScorecardCard result={result} examId={examId} onAgain={() => setResult(null)} onNext={next} />}
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// SPEAKING (Read Aloud / Describe Image / Speak Photo / Oxford prompt)
// ============================================================
export function SpeakingPlayer({ examId, typeId, item, meta, onResult, submitting, setSubmitting }) {
  const [phase, setPhase] = useState("idle"); // idle | prep | recording | submitting | done
  const [prep, setPrep] = useState(meta.prepSec || 0);
  const [sec, setSec] = useState(0);
  const prepTimerRef = useRef(null);
  const timerRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => () => {
    clearInterval(prepTimerRef.current);
    clearInterval(timerRef.current);
    try { mediaRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch {}
  }, []);

  const startPrep = () => {
    setPhase("prep");
    setPrep(meta.prepSec || 0);
    if (!meta.prepSec) return startRecording();
    prepTimerRef.current = setInterval(() => {
      setPrep((p) => {
        if (p <= 1) { clearInterval(prepTimerRef.current); startRecording(); return 0; }
        return p - 1;
      });
    }, 1000);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const m = new MediaRecorder(stream);
      mediaRef.current = m;
      chunksRef.current = [];
      m.ondataavailable = (e) => chunksRef.current.push(e.data);
      m.onstop = () => stream.getTracks().forEach((t) => t.stop());
      m.start();
      setPhase("recording");
      setSec(0);
      timerRef.current = setInterval(() => {
        setSec((s) => {
          if (s + 1 >= meta.recordSec) { stopAndSubmit(); return s; }
          return s + 1;
        });
      }, 1000);
    } catch {
      toast.error("Mic permission denied");
      setPhase("idle");
    }
  };

  const stopAndSubmit = async () => {
    clearInterval(timerRef.current);
    if (mediaRef.current?.state !== "inactive") mediaRef.current?.stop();
    setPhase("submitting");
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 300));
    try {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      const fd = new FormData();
      fd.append("file", blob, "answer.webm");
      fd.append("exam_id", examId);
      fd.append("type_id", typeId);
      fd.append("item_id", item.id);
      fd.append("duration_sec", String(sec));
      const r = await api.post("/exam/score-speaking", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onResult(r.data.result);
      setPhase("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scoring failed");
      setPhase("idle");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-3xl p-6 space-y-5">
      {/* Audio prompt (Repeat Sentence / Re-tell Lecture) */}
      {meta.hasAudio && (
        <AudioPrompt text={item.sentence || item.lecture_text} label={
          typeId === "repeat-sentence" ? "Listen — then repeat the sentence exactly" : "Listen to the lecture — then re-tell it in your own words"
        } />
      )}

      {/* Prompt content */}
      {item.passage && (
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Read this passage aloud</div>
          <div className="text-lg leading-relaxed text-[#003049]" data-testid="passage-text">{item.passage}</div>
        </div>
      )}
      {item.image_url && (
        <div className="rounded-2xl overflow-hidden border border-slate-200">
          <img src={item.image_url} alt="Prompt" className="w-full max-h-[360px] object-cover" data-testid="prompt-image" />
        </div>
      )}
      {item.prompt && (
        <div className="text-base text-slate-700">{item.prompt}</div>
      )}

      {/* Control */}
      <div className="flex items-center gap-4">
        {phase === "idle" && (
          <button onClick={startPrep} data-testid="speaking-start-btn"
            className="px-6 py-3 rounded-full bg-[#F77F00] hover:bg-[#D26900] text-white font-medium inline-flex items-center gap-2">
            <Mic className="w-4 h-4" /> Start {meta.prepSec ? "Preparation" : "Recording"}
          </button>
        )}
        {phase === "prep" && (
          <div className="flex items-center gap-3 px-5 py-3 rounded-full bg-amber-50 border border-amber-200 text-amber-700">
            <Clock className="w-4 h-4" /> <span className="font-semibold tabular-nums">{prep}s</span>
            <span className="text-sm">preparation — your microphone will start automatically</span>
          </div>
        )}
        {phase === "recording" && (
          <>
            <button onClick={stopAndSubmit} data-testid="speaking-stop-btn"
              className="px-6 py-3 rounded-full bg-slate-900 text-white font-medium inline-flex items-center gap-2">
              <MicOff className="w-4 h-4" /> Stop & Submit
            </button>
            <div className="flex-1">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                <span className="font-semibold text-[#F77F00]">Recording</span>
                <span className="tabular-nums">{sec}s / {meta.recordSec}s</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-[#F77F00]" style={{ width: `${(sec / meta.recordSec) * 100}%` }} />
              </div>
            </div>
          </>
        )}
        {phase === "submitting" && (
          <div className="flex items-center gap-3 text-slate-700">
            <Loader2 className="w-5 h-5 animate-spin text-[#F77F00]" />
            Scoring your response with AI examiner…
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// READ & COMPLETE (Duolingo) — random letters hidden, candidate fills
// ============================================================
export function ReadCompletePlayer({ examId, typeId, item, meta, onResult, submitting, setSubmitting }) {
  const [maskedIndices, setMaskedIndices] = useState([]);
  const [inputs, setInputs] = useState({});
  const [timeLeft, setTimeLeft] = useState(meta.durationSec || 180);
  const timerRef = useRef(null);

  // Generate mask deterministically per item id
  useEffect(() => {
    const ratio = item.missing_ratio || 0.35;
    const text = item.text || "";
    const idxs = [];
    // Skip first 6 chars to leave intro intact, then every 2nd letter approx.
    for (let i = 6; i < text.length; i++) {
      if (/[a-z]/i.test(text[i])) {
        // pseudo-deterministic: use char position mod
        if (((i * 7 + text.charCodeAt(i)) % 100) < ratio * 100) idxs.push(i);
      }
    }
    setMaskedIndices(idxs);
    setInputs({});
    setTimeLeft(meta.durationSec || 180);
  }, [item.id]); // eslint-disable-line

  useEffect(() => {
    timerRef.current = setInterval(() => setTimeLeft((t) => Math.max(0, t - 1)), 1000);
    return () => clearInterval(timerRef.current);
  }, [item.id]);

  const onChange = (i, val) => {
    const ch = val.slice(-1).toLowerCase();
    setInputs((s) => ({ ...s, [i]: ch }));
    // Auto-advance focus to next blank
    if (ch) {
      const nextIdx = maskedIndices.find((m) => m > i);
      if (nextIdx !== undefined) {
        const el = document.querySelector(`[data-testid="rc-input-${nextIdx}"]`);
        el?.focus();
      }
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const reconstructed = (item.text || "").split("").map((c, i) =>
        maskedIndices.includes(i) ? (inputs[i] || "_") : c
      ).join("");
      const r = await api.post("/exam/score-fill-blanks", {
        exam_id: examId, type_id: typeId, item_id: item.id, candidate_text: reconstructed,
      });
      onResult(r.data.result);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scoring failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-3xl p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-500">Fill in the missing letters</div>
        <div className="text-sm text-slate-700 tabular-nums">⏱ {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}</div>
      </div>
      <div className="text-lg leading-loose font-mono text-[#003049]" data-testid="read-complete-text">
        {(item.text || "").split("").map((c, i) => {
          if (!maskedIndices.includes(i)) return <span key={i}>{c}</span>;
          return (
            <input key={i}
              data-testid={`rc-input-${i}`}
              value={inputs[i] || ""}
              onChange={(e) => onChange(i, e.target.value)}
              className="inline-block w-6 mx-[1px] border-b-2 border-[#F77F00] text-center text-base bg-rose-50 focus:bg-rose-100 focus:outline-none"
              maxLength={1}
            />
          );
        })}
      </div>
      <div className="flex justify-end">
        <button onClick={submit} disabled={submitting} data-testid="rc-submit-btn"
          className="px-6 py-3 rounded-full bg-[#F77F00] hover:bg-[#D26900] text-white font-medium inline-flex items-center gap-2 disabled:opacity-50">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Submit answer
        </button>
      </div>
    </div>
  );
}

// ============================================================
// WRITING (Write Photo / Oxford writing-task)
// ============================================================
export function WritingPlayer({ examId, typeId, item, meta, onResult, submitting, setSubmitting }) {
  const [text, setText] = useState("");
  const [timeLeft, setTimeLeft] = useState(meta.durationSec || 300);
  const timerRef = useRef(null);

  useEffect(() => {
    setText("");
    setTimeLeft(meta.durationSec || 300);
    timerRef.current = setInterval(() => setTimeLeft((t) => Math.max(0, t - 1)), 1000);
    return () => clearInterval(timerRef.current);
  }, [item.id, meta.durationSec]);

  const submit = async () => {
    if (text.trim().length < 5) { toast.error("Please write at least a sentence"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/exam/score-writing", {
        exam_id: examId, type_id: typeId, item_id: item.id, text,
      });
      onResult(r.data.result);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scoring failed");
    } finally {
      setSubmitting(false);
    }
  };

  const wc = text.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="bg-white border border-slate-200 rounded-3xl p-6 space-y-5">
      {item.image_url && (
        <div className="rounded-2xl overflow-hidden border border-slate-200">
          <img src={item.image_url} alt="Prompt" className="w-full max-h-[280px] object-cover" data-testid="prompt-image" />
        </div>
      )}
      {item.prompt && <div className="text-base text-slate-700">{item.prompt}</div>}

      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-500">Write your response</div>
        <div className="text-sm text-slate-700 tabular-nums">⏱ {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")} · {wc} words</div>
      </div>
      <textarea
        data-testid="writing-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="w-full min-h-[220px] border border-slate-200 rounded-xl p-4 text-base focus:border-[#F77F00] focus:ring-2 focus:ring-rose-100 outline-none"
        placeholder="Start typing your response…"
      />
      <div className="flex justify-end">
        <button onClick={submit} disabled={submitting} data-testid="writing-submit-btn"
          className="px-6 py-3 rounded-full bg-[#F77F00] hover:bg-[#D26900] text-white font-medium inline-flex items-center gap-2 disabled:opacity-50">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Submit for AI grading
        </button>
      </div>
    </div>
  );
}

// ============================================================
// MCQ (Oxford reading)
// ============================================================
export function MCQPlayer({ examId, typeId, item, meta, onResult, submitting, setSubmitting }) {
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(meta.durationSec || 240);
  const timerRef = useRef(null);
  const isListening = typeId === "listening-mcq";

  useEffect(() => {
    setAnswers({});
    setTimeLeft(meta.durationSec || 240);
    timerRef.current = setInterval(() => setTimeLeft((t) => Math.max(0, t - 1)), 1000);
    return () => clearInterval(timerRef.current);
  }, [item.id, meta.durationSec]);

  const submit = async () => {
    const arr = item.questions.map((_, i) => answers[i] ?? -1);
    if (arr.includes(-1)) { toast.error("Please answer all questions"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/exam/score-mcq", {
        exam_id: examId, type_id: typeId, item_id: item.id, answers: arr,
      });
      onResult(r.data.result);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scoring failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="bg-white border border-slate-200 rounded-3xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-slate-500">{isListening ? "Listening passage" : "Passage"}</div>
        </div>
        {isListening ? (
          <>
            <AudioPrompt text={item.passage} label="Listen carefully — the audio plays only twice" maxPlays={2} />
            <div className="text-xs text-slate-400 italic">Transcript hidden during listening test</div>
          </>
        ) : (
          <div className="text-base leading-relaxed text-[#003049]" data-testid="mcq-passage">{item.passage}</div>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-3xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-slate-500">Questions</div>
          <div className="text-sm text-slate-700 tabular-nums">⏱ {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}</div>
        </div>
        {item.questions.map((q, qi) => (
          <div key={qi} className="rounded-xl border border-slate-200 p-4" data-testid={`mcq-q-${qi}`}>
            <div className="font-semibold text-[#003049] mb-3 text-sm">{qi + 1}. {q.q}</div>
            <div className="space-y-2">
              {q.choices.map((c, ci) => {
                const isSel = answers[qi] === ci;
                return (
                  <label key={ci} className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border ${
                    isSel ? "bg-rose-50 border-[#F77F00]" : "bg-white border-slate-200 hover:border-slate-300"
                  }`}>
                    <input type="radio" className="mt-1"
                      data-testid={`mcq-q-${qi}-choice-${ci}`}
                      checked={isSel}
                      onChange={() => setAnswers((s) => ({ ...s, [qi]: ci }))}
                    />
                    <span className="text-sm">{c}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
        <button onClick={submit} disabled={submitting} data-testid="mcq-submit-btn"
          className="w-full px-6 py-3 rounded-full bg-[#F77F00] hover:bg-[#D26900] text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Submit answers
        </button>
      </div>
    </div>
  );
}

// ============================================================
// LISTEN & TYPE (Duolingo) + AudioPrompt helper
// ============================================================
function AudioPrompt({ text, label = "Listen", maxPlays = 99 }) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [plays, setPlays] = useState(0);
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);

  useEffect(() => () => {
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
  }, []);

  const ensureLoaded = async () => {
    if (audioRef.current) return audioRef.current;
    setLoading(true);
    try {
      const r = await api.post("/ai/tts", { text, voice: "nova" }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      blobUrlRef.current = url;
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => setPlaying(false);
      return a;
    } catch (e) {
      toast.error("Audio unavailable");
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const play = async () => {
    if (plays >= maxPlays) return;
    const a = await ensureLoaded();
    setPlaying(true);
    setPlays((p) => p + 1);
    a.currentTime = 0;
    a.play().catch(() => setPlaying(false));
  };

  const exhausted = plays >= maxPlays;
  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#003049] to-[#0C4B66] text-white p-5 flex items-center gap-4" data-testid="audio-prompt">
      <button
        onClick={play}
        disabled={loading || playing || exhausted}
        data-testid="audio-play-btn"
        className="w-14 h-14 rounded-full bg-white text-[#003049] flex items-center justify-center hover:scale-105 transition disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> :
         playing ? <span className="w-3 h-3 rounded-sm bg-[#003049]" /> :
         <span className="ml-1 w-0 h-0 border-l-[12px] border-l-[#003049] border-y-[8px] border-y-transparent" />}
      </button>
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/70">{label}</div>
        <div className="text-sm mt-1">
          {playing ? "Playing…" : exhausted ? `Plays exhausted (${plays}/${maxPlays})` : `Tap to play${maxPlays < 99 ? ` (${plays}/${maxPlays} used)` : ""}`}
        </div>
      </div>
    </div>
  );
}

export function ListenTypePlayer({ examId, typeId, item, meta, onResult, submitting, setSubmitting }) {
  const [text, setText] = useState("");
  const [timeLeft, setTimeLeft] = useState(meta.durationSec || 120);
  const timerRef = useRef(null);

  useEffect(() => {
    setText("");
    setTimeLeft(meta.durationSec || 120);
    timerRef.current = setInterval(() => setTimeLeft((t) => Math.max(0, t - 1)), 1000);
    return () => clearInterval(timerRef.current);
  }, [item.id, meta.durationSec]);

  const submit = async () => {
    if (!text.trim()) { toast.error("Type what you heard before submitting"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/exam/score-listen-type", {
        exam_id: examId, type_id: typeId, item_id: item.id, candidate_text: text,
      });
      onResult(r.data.result);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scoring failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-3xl p-6 space-y-5">
      <AudioPrompt text={item.sentence} label="Listen carefully — you can replay up to 3 times" maxPlays={3} />
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-500">Type the sentence exactly</div>
        <div className="text-sm text-slate-700 tabular-nums">⏱ {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}</div>
      </div>
      <textarea
        data-testid="listen-type-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="w-full min-h-[120px] border border-slate-200 rounded-xl p-4 text-base focus:border-[#F77F00] focus:ring-2 focus:ring-rose-100 outline-none"
        placeholder="Type exactly what you heard…"
      />
      <div className="flex justify-end">
        <button onClick={submit} disabled={submitting} data-testid="listen-type-submit-btn"
          className="px-6 py-3 rounded-full bg-[#F77F00] hover:bg-[#D26900] text-white font-medium inline-flex items-center gap-2 disabled:opacity-50">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Submit
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Scorecard
// ============================================================
function ScorecardCard({ result, examId, onAgain, onNext }) {
  return (
    <div className="bg-gradient-to-br from-[#003049] to-[#0C4B66] text-white rounded-3xl p-6" data-testid="exam-result">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-white/70 mb-1">Your score</div>
          <div className="font-display text-6xl mb-1">{result.overall ?? "—"}</div>
          {result.score_pct !== undefined && (
            <div className="text-sm text-white/70">{result.correct}/{result.total} correct · {result.score_pct}%</div>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={onAgain} className="px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 text-sm inline-flex items-center gap-2">
            <RotateCcw className="w-4 h-4" /> Try again
          </button>
          <button onClick={onNext} className="px-4 py-2 rounded-full bg-white text-[#003049] text-sm font-semibold inline-flex items-center gap-2">
            Next question <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Sub-scores */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.entries(result).filter(([k, v]) =>
          typeof v === "object" && v && (v.score !== undefined || v.level !== undefined)
        ).map(([k, v]) => (
          <div key={k} className="rounded-xl bg-white/10 backdrop-blur p-3">
            <div className="text-[10px] uppercase tracking-wider text-white/60">{k.replaceAll("_", " ")}</div>
            <div className="font-display text-xl mt-1">{v.score ?? v.level}</div>
            {v.comment && <div className="text-[11px] text-white/70 mt-1 leading-snug">{v.comment}</div>}
          </div>
        ))}
      </div>

      {/* Per-question feedback for MCQ */}
      {result.per_question && (
        <div className="mt-6 space-y-2">
          {result.per_question.map((pq, i) => (
            <div key={i} className="flex items-center gap-3 rounded-lg bg-white/10 px-3 py-2 text-sm">
              <CheckCircle2 className={`w-4 h-4 ${pq.is_correct ? "text-emerald-300" : "text-rose-300"}`} />
              <span className="flex-1">{pq.q}</span>
              <span className="text-white/70">{pq.is_correct ? "Correct" : `→ ${pq.correct_text}`}</span>
            </div>
          ))}
        </div>
      )}

      {result.feedback && (
        <div className="mt-6 rounded-xl bg-white/10 p-4 text-sm leading-relaxed">{result.feedback}</div>
      )}

      {result.suggestions?.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wider text-white/70 mb-2">Action items</div>
          <ul className="space-y-1 text-sm">
            {result.suggestions.map((s, i) => <li key={i}>• {s}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
